import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  createGroup,
  groupFormQuery,
  groupQuery,
  saveGroup,
  setGroupState,
  usersGroupsQuery,
  type Choice,
  type UsersShell,
} from '../../api/settingsUsers'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { HourlyRate } from './HourlyRate'
import { esc, failed, tableRequest } from './shared'

/**
 * Account Groups - `groups/index.php` (lista + modala "New group"),
 * `groups/tabList.php` (randul desfasurat) si `groups/editForm.php`.
 *
 * Coloanele sunt cele din `GroupsModel` (`public/js/settings/users.js`);
 * butonul de pe rand e Disable cand grupul e activ si Enable cand nu e.
 */

const ORDER_NAMES = ['', 'group_name', 'status', 'group_email', 'dispatching_email', 'phone', '']

function groupColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.ID)}" data-type=""><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1,
      title: 'Group name',
      orderable: true,
      visible: true,
      html: (row) => `<strong style="color:#2b6497 ">${esc(row.group_name)}</strong>`,
    },
    {
      index: 2,
      title: 'Status',
      orderable: true,
      visible: true,
      html: (row) =>
        Number(row.status) === 1
          ? '<span class="label label-success">Active</span>'
          : '<span class="label label-warning">Inactive</span>',
    },
    { index: 3, title: 'Group Email', orderable: true, visible: true, html: (row) => esc(row.group_email) },
    {
      index: 4,
      title: 'Dispatching Email',
      orderable: true,
      visible: true,
      html: (row) => esc(row.dispatching_email),
    },
    { index: 5, title: 'Phone', orderable: true, visible: true, html: (row) => esc(row.phone) },
    {
      index: 6,
      title: 'Actions',
      orderable: false,
      visible: true,
      html: (row) => {
        const id = Number(row.ID)
        return Number(row.status) === 1
          ? `<button class="btn btn-xs btn-danger od-disable-group" data-group="${id}"><i class="fas fa-times-circle" aria-hidden="true"></i> Disable</button>`
          : `<button class="btn btn-xs btn-success od-enable-group" data-group="${id}"><i class="fas fa-undo" aria-hidden="true"></i> Enable</button>`
      },
    },
  ]
}

export function GroupsPanel({ shell }: { shell: UsersShell }) {
  const client = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [state, setState] = useTableState('settings-users-groups', {
    start: 0,
    length: 25,
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => groupColumns(), [])
  const table = useQuery({
    ...usersGroupsQuery(tableRequest(state, ORDER_NAMES, 'ID')),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const reload = () => void client.invalidateQueries({ queryKey: ['settings', 'users', 'groups'] })

  const flip = (id: number, enabled: boolean) => {
    void setGroupState(id, enabled)
      .then(() => {
        toast(enabled ? 'Group enabled.' : 'Group disabled.', 'Success')
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <div id="content">
      {shell.may['settings.user_accounts.edit'] && (
        <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setAdding(true)}>
          <i className="fas fa-plus" aria-hidden="true"></i> New group
        </button>
      )}
      <h4>
        <strong>Groups</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage <strong>account</strong> groups
      </label>
      <hr />

      <div
        onClick={(event) => {
          const target = event.target as HTMLElement
          const off = target.closest('.od-disable-group')
          if (off) {
            flip(Number(off.getAttribute('data-group') ?? 0), false)
            return
          }
          const on = target.closest('.od-enable-group')
          if (on) flip(Number(on.getAttribute('data-group') ?? 0), true)
        }}
      >
        <DataTable
          id="settings-users-groups"
          columns={columns}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? failed(table.error) : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => `row_${Number(row.ID)}`}
          emptyHint="A group is a set of people who take tickets together."
          details={(row) => <GroupWorkspace groupId={Number(row.ID)} onSaved={reload} />}
        />
      </div>

      <NewGroupModal
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={() => {
          setAdding(false)
          reload()
        }}
      />
    </div>
  )
}

/** Randul desfasurat: `groups/tabList.php` are o singura fila, "Edit group". */
function GroupWorkspace({ groupId, onSaved }: { groupId: number; onSaved(): void }) {
  const page = useQuery(groupQuery(groupId))

  if (page.error) return <div className="alert alert-danger">{failed(page.error)}</div>
  if (!page.data) return <Spinner />

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          <li>
            <a href={`/app/settings/users_accounts/groups#${groupId}`} className="action_button active" onClick={(e) => e.preventDefault()}>
              Edit group
            </a>
          </li>
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          <EditGroupForm
            groupId={groupId}
            group={page.data.group}
            ticketTypes={page.data.ticketTypes}
            selected={page.data.selectedTypes}
            onSaved={onSaved}
          />
          {/* Tariful orar al grupului (numai v4): costul pe tehnician din Rapoarte. */}
          <HourlyRate type="group" id={groupId} />
        </div>
      </div>
    </div>
  )
}

type GroupFields = {
  name: string
  email: string
  dispatchingEmail: string
  phone: string
  level: number
  quickChatSupport: boolean
  assignChatTicket: boolean
  quickEscalation: boolean
  startHour: string
  endHour: string
  types: number[]
}

function EditGroupForm({
  groupId,
  group,
  ticketTypes,
  selected,
  onSaved,
}: {
  groupId: number
  group: {
    name: string
    email: string
    dispatchingEmail: string
    phone: string
    level: number
    quickChatSupport: boolean
    assignChatTicket: boolean
    quickEscalation: boolean
    startHour: string
    endHour: string
  }
  ticketTypes: Choice[]
  selected: number[]
  onSaved(): void
}) {
  const client = useQueryClient()
  const [form, setForm] = useState<GroupFields>({ ...group, types: selected })
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setForm({ ...group, types: selected })
  }, [group, selected])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void saveGroup(groupId, {
      group_name: form.name,
      group_email: form.email,
      group_dispatching_email: form.dispatchingEmail,
      group_phone: form.phone,
      level: form.level,
      has_quick_chat_support: form.quickChatSupport ? 'on' : '',
      assign_chat_ticket: form.assignChatTicket ? 'on' : '',
      quick_escalation: form.quickEscalation ? 'on' : '',
      'assign_chat_ticket_program[start_hour]': form.startHour,
      'assign_chat_ticket_program[end_hour]': form.endHour,
      ticket_types: form.types,
    })
      .then(() => {
        toast('Changes saved', 'Success')
        void client.invalidateQueries({ queryKey: ['settings', 'users', 'groups'] })
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <form role="form" id="edit_group_form" method="post" onSubmit={submit}>
      <div className="form-group row">
        <div className="col-md-6">
          <label className="control-label">
            Group Name <span className="required">*</span>
          </label>
          <input
            className="form-control"
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="col-md-6">
          <label className="control-label">Group Email</label>
          <input
            className="form-control"
            type="text"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
      </div>

      <div className="form-group row">
        <div className="col-md-6">
          <label className="control-label">Dispatching Email</label>
          <input
            placeholder="Email"
            className="form-control"
            type="email"
            value={form.dispatchingEmail}
            onChange={(e) => setForm({ ...form, dispatchingEmail: e.target.value })}
          />
        </div>
        <div className="col-md-6">
          <label className="control-label">Phone</label>
          <input
            className="form-control"
            type="text"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>
      </div>

      <div className="form-group row">
        <div className="col-md-6">
          <label className="control-label">Pass Safe Level</label>
          <select
            className="form-control"
            value={String(form.level)}
            onChange={(e) => setForm({ ...form, level: Number(e.target.value) })}
          >
            <option value="0">No level</option>
            <option value="1">Level 1</option>
            <option value="2">Level 2</option>
            <option value="3">Level 3</option>
          </select>
        </div>
        <div className="col-md-6">
          <label className="control-label">Ticket types</label>
          <TicketTypePicker
            types={ticketTypes}
            chosen={form.types}
            onChange={(types) => setForm({ ...form, types })}
          />
        </div>
      </div>

      <div className="form-group row">
        <div className="col-md-6">
          <label className="control-label" htmlFor="assign_chat_ticket">
            Assign chat ticket
          </label>
          <div>
            <input
              type="checkbox"
              id="assign_chat_ticket"
              checked={form.assignChatTicket}
              onChange={(e) => setForm({ ...form, assignChatTicket: e.target.checked })}
            />
          </div>
        </div>

        <div className="col-md-6">
          <label className="control-label">Assign chat ticket program</label>
          <div className="row">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-addon font-12">from</span>
                <input
                  className="form-control input-sm"
                  type="time"
                  value={form.startHour}
                  onChange={(e) => setForm({ ...form, startHour: e.target.value })}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-addon font-12">to</span>
                <input
                  className="form-control input-sm"
                  type="time"
                  value={form.endHour}
                  onChange={(e) => setForm({ ...form, endHour: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="form-group row">
        <div className="col-md-6">
          <label className="control-label" htmlFor="has_quick_chat_support">
            Enable quick chat support
          </label>
          <div>
            <input
              type="checkbox"
              id="has_quick_chat_support"
              checked={form.quickChatSupport}
              onChange={(e) => setForm({ ...form, quickChatSupport: e.target.checked })}
            />
          </div>
        </div>
        <div className="col-md-6">
          <label className="control-label" htmlFor="quick_escalation">
            Enable quick escalation
          </label>
          <div>
            <input
              type="checkbox"
              id="quick_escalation"
              checked={form.quickEscalation}
              onChange={(e) => setForm({ ...form, quickEscalation: e.target.checked })}
            />
          </div>
        </div>
      </div>

      <div className="margin-top-10">
        <button className="btn btn-success" type="submit" disabled={busy}>
          {busy ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </form>
  )
}

/** `select[multiple]` din machete - `selectpicker` in portal, aici cum e. */
function TicketTypePicker({
  types,
  chosen,
  onChange,
}: {
  types: Choice[]
  chosen: number[]
  onChange(next: number[]): void
}) {
  return (
    <select
      className="form-control"
      multiple
      required
      size={Math.min(6, Math.max(3, types.length))}
      value={chosen.map(String)}
      onChange={(event) =>
        onChange(Array.from(event.currentTarget.selectedOptions).map((option) => Number(option.value)))
      }
    >
      {types.map((type) => (
        <option key={type.id} value={type.id}>
          {type.name}
        </option>
      ))}
    </select>
  )
}

function NewGroupModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const form = useQuery({ ...groupFormQuery, enabled: open })
  const [fields, setFields] = useState<GroupFields>({
    name: '',
    email: '',
    dispatchingEmail: '',
    phone: '',
    level: 0,
    quickChatSupport: false,
    assignChatTicket: false,
    quickEscalation: false,
    startHour: '00:00',
    endHour: '00:00',
    types: [],
  })
  const [busy, setBusy] = useState(false)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void createGroup({
      group_name: fields.name,
      group_email: fields.email,
      group_dispatching_email: fields.dispatchingEmail,
      group_phone: fields.phone,
      has_quick_chat_support: fields.quickChatSupport ? 'on' : '',
      assign_chat_ticket: fields.assignChatTicket ? 'on' : '',
      quick_escalation: fields.quickEscalation ? 'on' : '',
      'assign_chat_ticket_program[start_hour]': fields.startHour,
      'assign_chat_ticket_program[end_hour]': fields.endHour,
      ticket_types: fields.types,
    })
      .then(() => {
        toast('Group was saved!', 'Success')
        setFields({ ...fields, name: '', email: '', dispatchingEmail: '', phone: '', types: [] })
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal id="newGroupModal" open={open} onClose={onClose} title="New group" labelledBy="newGroupModalLabel" large>
      <form className="form-horizontal" method="post" id="newGroupForm" onSubmit={submit}>
        <div className="form-group">
          <label className="col-sm-4 control-label">
            Group name <span className="required">*</span>
          </label>
          <div className="col-sm-8">
            <input
              type="text"
              className="form-control input-sm"
              required
              value={fields.name}
              onChange={(e) => setFields({ ...fields, name: e.target.value })}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-4 control-label">Group email</label>
          <div className="col-sm-8">
            <input
              type="email"
              className="form-control input-sm"
              value={fields.email}
              onChange={(e) => setFields({ ...fields, email: e.target.value })}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-4 control-label">Group dispatching email</label>
          <div className="col-sm-8">
            <input
              type="email"
              className="form-control input-sm"
              value={fields.dispatchingEmail}
              onChange={(e) => setFields({ ...fields, dispatchingEmail: e.target.value })}
            />
            <span className="help-block">
              Used to dispatch tickets <i className="fa fa-info"></i>
            </span>
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-4 control-label">Phone</label>
          <div className="col-sm-8">
            <input
              type="text"
              className="form-control input-sm"
              value={fields.phone}
              onChange={(e) => setFields({ ...fields, phone: e.target.value })}
            />
          </div>
        </div>

        <div className="form-group row">
          <label className="col-sm-4 control-label">Ticket types</label>
          <div className="col-sm-8">
            <TicketTypePicker
              types={form.data?.ticketTypes ?? []}
              chosen={fields.types}
              onChange={(types) => setFields({ ...fields, types })}
            />
          </div>
        </div>

        <div className="form-group row">
          <label className="col-sm-4 control-label pt-0">Enable quick chat support</label>
          <div className="col-md-8">
            <div>
              <input
                type="checkbox"
                checked={fields.quickChatSupport}
                onChange={(e) => setFields({ ...fields, quickChatSupport: e.target.checked })}
              />
            </div>
          </div>
        </div>

        <div className="form-group row">
          <label className="col-sm-4 control-label pt-0">Assign chat ticket</label>
          <div className="col-md-8">
            <div>
              <input
                type="checkbox"
                checked={fields.assignChatTicket}
                onChange={(e) => setFields({ ...fields, assignChatTicket: e.target.checked })}
              />
            </div>
          </div>
        </div>

        <div className="form-group row">
          <label className="col-sm-4 control-label pt-0">Enable quick escalation</label>
          <div className="col-md-8">
            <div>
              <input
                type="checkbox"
                checked={fields.quickEscalation}
                onChange={(e) => setFields({ ...fields, quickEscalation: e.target.checked })}
              />
            </div>
          </div>
        </div>

        <div className="form-group row">
          <label className="col-sm-4 control-label pt-0">Assign chat ticket program</label>
          <div className="col-md-8">
            <div className="row">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-addon font-12">from</span>
                  <input
                    className="form-control input-sm"
                    type="time"
                    value={fields.startHour}
                    onChange={(e) => setFields({ ...fields, startHour: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-addon font-12">to</span>
                  <input
                    className="form-control input-sm"
                    type="time"
                    value={fields.endHour}
                    onChange={(e) => setFields({ ...fields, endHour: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="form-group">
          <div className="col-sm-offset-4 col-sm-8 aright">
            <button type="button" className="btn btn-default" data-dismiss="modal" onClick={onClose}>
              Close
            </button>{' '}
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
